import {
  IButtonModeEnum,
  IButtonSizeEnum,
  IButtonStyleEnum,
  IButtonVariantsEnum,
} from "@molecules/button";

import type { ParseKeys } from "i18next";

import type { IFastButtonVariantsData } from "@fastFields/fastButton/types";

export const fastButtonVariants = (
  entityName?: ParseKeys<"entity">,
): IFastButtonVariantsData => ({
  decrease: {
    startIcon: "SubtractLine",
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  increase: {
    startIcon: "AddLine",
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  saveAndSend: {
    label: (t) => t("register"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
  },
  cancelAndBack: {
    label: (t) => t("cancelAndBack"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
  },
  close: {
    label: (t) => t("close"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
  },
  save: {
    label: (t) => t("saveChanges"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
  },
  back: {
    label: (t) => t("back"),
    startIcon: "ArrowRightLine",
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
  },
  cancel: {
    label: (t) => t("cancel"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
  },
  logout: {
    label: (t) => t("logout"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.Small,
    startIcon: "LogoutBoxLine",
  },
  cancelOperation: {
    label: (t) => t("cancelOperation"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
    startIcon: "CloseLine",
  },
  saveChanges: {
    label: (t) => t("saveChanges"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
    startIcon: "CheckLine",
  },

  deleteOf: {
    label: (t) =>
      t("deleteOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
  },
  copyID: {
    label: (t) => t("copyID"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.X_Small,
  },
  addNewOf: {
    label: (t) =>
      t("addNewOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  createNewOf: {
    label: (t) =>
      t("addNew", { name: entityName ? t(`entity:${entityName}`) : "" }),
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  resetPassword: {
    label: (t) => t("resetPassword"),
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Filled,
    endIcon: "ArrowLeftSLine",
  },
  activate: {
    label: (t) => t("activate"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Success,
  },
  deactivate: {
    label: (t) => t("deActivate"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Error,
  },
  sendMessage: {
    label: (t) => t("sendMessage"),
    startIcon: "Message3Line",
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  backFancy: {
    label: (t) => t("backFancy"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  done: {
    label: (t) => t("done"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  login: {
    label: (t) => t("page:auth.login.actions.login"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },
  resetPasswordLogin: {
    label: (t) => t("page:auth.resetPassword.back.text"),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },
  returnToMainPage: {
    label: (t) => t("returnToMainPage"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  sendNewNotif: {
    label: (t) => t("sendNewNotif"),
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  viewAll: {
    label: (t) => t("viewAll"),
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Basic,
  },
  approveOf: {
    label: (t) =>
      t("approveOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Success,
    size: IButtonSizeEnum.X_Small,
  },
  disapproveOf: {
    label: (t) =>
      t("disapproveOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
  },
  sendMessageTo: {
    label: (t) =>
      t("sendMessageTo", { name: entityName ? t(`entity:${entityName}`) : "" }),
    startIcon: "SendPlane2Line",
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  sendMessageToIcon: {
    startIcon: "SendPlane2Line",
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Stroke,
  },
  loadMore: {
    label: (t) => t("loadMore"),
    size: IButtonSizeEnum.X_Small,
    startIcon: "AddLine",
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
  },
  sendFile: {
    label: (t) => t("sendFile"),
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "SendPlane2Line",
  },
  next: {
    label: (t) => t("page:auth.signUpPage.actions.next"),
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
  },
  tryAgain: {
    label: (t) => t("retry"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  returnToMainPagePrimary: {
    label: (t) => t("returnToMainPage"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  contact: {
    label: (t) => t("contact"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  submitAndSendCode: {
    label: (t) => t("page:auth.codeVerification.actions.submitCode"),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },
  confirm: {
    label: (t) => t("confirm"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Success,
    size: IButtonSizeEnum.X_Small,
    startIcon: "CheckLine",
  },
  reject: {
    label: (t) => t("reject"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
    startIcon: "CloseLine",
  },

  viewDoc: {
    startIcon: "AuctionLine",
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  edit: {
    label: (t) => t("doEdit"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
    startIcon: "PencilLine",
  },
  closeAlert: {
    startIcon: "CloseLine",
    styleType: IButtonStyleEnum.Ghost,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Medium,
  },
  deleteAllFilters: {
    endIcon: "FilterOffLine",
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Stroke,
    label: (t) => t("deleteAllFilters"),
  },
  onlyView: {
    label: (t) => t("onlyView"),
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.X_Small,
    styleType: IButtonStyleEnum.Stroke,
  },
  register: {
    label: (t) => t("registering"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    styleType: IButtonStyleEnum.Filled,
  },
  backToLogin: {
    label: (t) => t("backToLogin"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    styleType: IButtonStyleEnum.Stroke,
  },
  viewImage: {
    label: (t) => t("viewImage"),
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Stroke,
    size: IButtonSizeEnum.X_Small,
  },
  verified: {
    startIcon: "CheckLine",
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  completeProfile: {
    label: (t) => t("completeProfile"),
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Stroke,
    size: IButtonSizeEnum.X_Small,
  },
  sendLetter: {
    startIcon: "DraftLine",
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  finalizeLetter: {
    startIcon: "CheckLine",
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Ghost,
  },
  addToList: {
    label: (t) => t("addToList"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  viewUserProfile: {
    label: (t) => t("action:viewUserProfile"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.X_Small,
    startIcon: "AccountCircleLine",
  },
  whatsUp: {
    label: (t) => t("whatsUp"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Secondary,
    size: IButtonSizeEnum.X_Small,
  },
  cancelCurrentSubscription: {
    label: (t) => t("cancelCurrentSubscription"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
    startIcon: "CloseCircleLine",
  },
  renewalSubscription: {
    label: (t) => t("renewalSubscription"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Success,
    size: IButtonSizeEnum.X_Small,
    startIcon: "RefreshLine",
  },
  cancelSubscription: {
    label: (t) => t("cancelSubscription"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Error,
  },
  payment: {
    label: (t) => t("payment"),
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
  },
  confirmPrompt: {
    label: (t) => t("confirmPayment"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
  },
  showInfo: {
    label: (t) => t("showInfo"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Secondary,
    styleType: IButtonStyleEnum.Fancy,
  },
  confirmSale: {
    label: (t) => t("confirmSale"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "ShoppingCartLine",
  },
  deleteSaleInvoice: {
    label: (t) => t("deleteSaleInvoice"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Error,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "DeleteBinLine",
  },
  deletePurchaseInvoice: {
    label: (t) => t("deletePurchaseInvoice"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Error,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "DeleteBinLine",
  },
  customerDebtPayment: {
    label: (t) => t("customerDebtPayment"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Secondary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "MoneyDollarCircleLine",
  },
  clearAll: {
    label: (t) => t("clearAll"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Basic,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "EraserLine",
  },
  addProductToList: {
    label: (t) => t("addProductToList"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Secondary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "EraserLine",
  },
  confirmPurchase: {
    label: (t) => t("confirmPurchase"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "ShoppingBagLine",
  },
  submitReturn: {
    label: (t) => t("submitReturn"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "ShoppingCartLine",
  },
  returnsOfGoods: {
    label: (t) => t("returnsOfGoods"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "RestartLine",
  },
  fullDebtSettlement: {
    label: (t) => t("fullDebtSettlement"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "MoneyDollarCircleLine",
  },
});
